import type { ComponentType } from 'preact';
import { cx } from './internal';

interface VDataGridRow {
  /** Muted-text label on the left of the row. */
  label: string;
  /** Bold tabular-numeric value on the right. */
  value: string | number;
  /** Optional lucide-style leading icon component.
   *  Size is typed as `number | string` to match lucide-preact's
   *  LucideIcon surface — the row invokes it with a number. */
  icon?: ComponentType<{ size?: number | string }>;
}

interface VDataGridProps {
  rows: VDataGridRow[];
  /** `compact` = py-2 (denser tables); `comfortable` = py-3 (default). */
  variant?: 'compact' | 'comfortable';
  className?: string;
}

const PADDING = {
  compact: 'py-2',
  comfortable: 'py-3'
} as const;

/**
 * Read-only key/value list in a rounded card. Hairline separators
 * between rows. Used for "summary" / "details" / "stats" surfaces.
 *
 *   <VDataGrid rows={[
 *     { label: 'Calories', value: '420 kcal', icon: Flame },
 *     { label: 'Protein',  value: '32 g',     icon: Dumbbell },
 *     { label: 'Carbs',    value: '54 g' }
 *   ]} />
 */
export function VDataGrid({ rows, variant = 'comfortable', className }: VDataGridProps) {
  if (!rows || rows.length === 0) return null;
  return (
    <div
      // A2 — surface from the blueprint card token (genuine palette-blind grid).
      style={{ background: 'var(--color-bg-alt, #ffffff)' }}
      className={cx(
        // `overflow-hidden` clips the corner radius on the first/last rows.
        'rounded-2xl border border-black/[0.06] overflow-hidden min-w-0',
        'shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_-2px_rgba(0,0,0,0.06)]',
        'dark:border-white/[0.08] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_4px_12px_-2px_rgba(0,0,0,0.5)]',
        // `divide-y` paints hairlines between adjacent children — no
        // need to mark the last row separately.
        'divide-y divide-black/[0.06] dark:divide-white/[0.08]',
        className
      )}
    >
      {rows.map((row, i) => {
        const Icon = row.icon;
        return (
          <div
            key={`${row.label}-${i}`}
            className={cx(
              'flex items-center gap-3 px-4 min-w-0',
              PADDING[variant]
            )}
          >
            {Icon ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--color-primary-muted)] text-[var(--color-primary-active)]">
                <Icon size={16} />
              </div>
            ) : null}
            <div className="flex-1 min-w-0 text-sm text-[var(--color-fg-muted)] truncate">
              {row.label}
            </div>
            <div className="shrink-0 text-sm font-semibold tabular-nums text-[var(--color-fg)]">
              {row.value}
            </div>
          </div>
        );
      })}
    </div>
  );
}
